import { Component, Input, OnInit } from '@angular/core';
import { EMPTY, Subscription, forkJoin } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { expand, finalize, pairwise } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import moment from 'moment';

import { ChartDataset, ChartLabel } from 'src/app/core/entities/chart-dataset';
import { AutoUnsub } from 'src/app/core/decorators/auto-unsub.decorator';
import { ChartDataPoint } from 'src/app/core/entities/chart-data-point.entity';
import { ChartDescription } from 'src/app/core/entities/chart-description';
import { ChartEvaluation } from 'src/app/core/entities/chart-evaluation';
import { ChartService } from 'src/app/core/services/chart.service';
import { EvaluationService } from 'src/app/core/services/evaluation.service';
import { Feature } from 'src/app/core/entities/feature.entity';
import { FeatureService } from 'src/app/core/services/feature.service';
import { Patient } from 'src/app/core/entities/patient.entity';

@Component({
  selector: 'app-cognition',
  templateUrl: './cognition.component.html',
  styleUrls: ['./cognition.component.css'],
})
@AutoUnsub()
export class CognitionComponent implements OnInit {
  @Input() public patientProfile: Patient = new Patient();
  private _observers: Subscription = new Subscription();
  private _features: Feature[] = [];
  public evaluation: ChartEvaluation = {
    evaluationCode: EvaluationService.EVALUATION_COGNITION,
    features: [
      {
        featureId: FeatureService.FEATURE_CPS_STD_AR_RT_MEAN,
        label: [
          $localize`:@@patient_evaluationCognition_featureCPSSTDARRTMeanLabelPart1:Reaction time`,
          $localize`:@@patient_evaluationCognition_featureCPSSTDARRTMeanLabelPart2:(Late of response in ms)`,
        ],
        min: -5000,
        max: 0,
        isMinLimited: true,
        chartDatasetOption: {
          type: 'line',
          color: '#0e5cdd',
          tooltip: {
            label: $localize`:@@patient_evaluationCognition_featureCPSSTDARRTMeanTooltipLabel:Reaction time`,
            unit: 'ms',
          },
          pointStyle: 'circle',
        },
      },
      {
        featureId: FeatureService.FEATURE_CPS_STD_ACC,
        label: $localize`:@@patient_evaluationCognition_featureCPSSTDACCLabel:Rate of correct responses`,
        min: 0,
        max: 100,
        chartDatasetOption: {
          type: 'line',
          color: '#6225e1',
          tooltip: {
            label: $localize`:@@patient_evaluationCognition_featureCPSSTDACCTooltipLabel:Rate (in %)`,
            unit: '%',
          },
          hidden: true,
          nbSteps: 4,
          pointStyle: 'circle',
          nbDecimalDigits: 0,
          axis: {
            id: 'cpsStdAcc',
            type: 'linear',
            position: 'right',
            beginAtZero: true,
          },
        },
      },
      {
        featureId: FeatureService.FEATURE_CPS_STD_CRC,
        label: $localize`:@@patient_evaluationCognition_featureCPSSTDCRCLabel:Correct responses count`,
        min: 0,
        max: 110,
        chartDatasetOption: {
          type: 'line',
          color: '#0dc49c',
          tooltip: {
            label: $localize`:@@patient_evaluationCognition_featureCPSSTDCRCTooltipLabel:Correct responses count`,
            unit: '',
          },
          hidden: true,
          nbSteps: 4,
          pointStyle: 'circle',
          nbDecimalDigits: 0,
          axis: {
            id: 'cpsStdCrc',
            type: 'linear',
            position: 'right',
            beginAtZero: true,
          },
        },
      },
    ],
    horizontalLimitLines: [
      {
        label: $localize`:@@patient_evaluationCognition_horizontalLimitLineYLabel:Maximum speed`,
        color: '#3d3d40',
        assignToAxisIds: {
          y: 0,
        },
      },
    ],
  };
  public datasets: ChartDataset[] = [];
  public xLabels: ChartLabel[] = [];
  public chartDescription: ChartDescription;
  public dateRangeForm: FormGroup;
  public loading = true;
  public loadingChart = true;

  constructor(
    private featureService: FeatureService,
    private chartService: ChartService,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.prepareDateRangeForm();

    this.resetChartDescription();
  }

  prepareDateRangeForm(): void {
    const startDate = moment.utc().subtract(6, 'months').format('MM/YYYY');
    const endDate = moment.utc().format('MM/YYYY');

    this.dateRangeForm = this.formBuilder.group({
      startDate: [
        startDate,
        [
          Validators.required,
          Validators.pattern(/^(0[1-9]|1[012])\/[0-9]{4}$/),
        ],
      ],
      endDate: [
        endDate,
        [
          Validators.required,
          Validators.pattern(/^(0[1-9]|1[012])\/[0-9]{4}$/),
        ],
      ],
    });

    this._observers.add(
      this.dateRangeForm.valueChanges.pipe(pairwise()).subscribe({
        next: ([previousRes, res]) => this.submitDateRangeForm(),
      })
    );

    this.loading = false;
  }

  changeDateRange(
    event: { monthIndex: number; year: number },
    key: string
  ): void {
    const value = moment
      .utc([event.year, event.monthIndex, 1])
      .format('MM/YYYY');

    this.dateRangeForm.get(key).setValue(value);
  }

  getDateYear(key: string): number {
    let splitDate = this.dateRangeForm.get(key).value.split('/');
    return splitDate[1];
  }

  getDateMonth(key: string): number {
    let splitDate = this.dateRangeForm.get(key).value.split('/');
    return parseInt(splitDate[0], 10) - 1;
  }

  submitDateRangeForm(): void {
    if (this.dateRangeForm.valid) {
      this.loadingChart = true;
      this.setChart();
    }
  }

  resetChartDescription(): void {
    this.chartDescription = {
      evaluationCode: this.evaluation.evaluationCode,
      evaluationLabel: $localize`:@@patient_evaluationCognition_chartDescriptionLabel:of cognitive processing speed`,
      measuringPointsTotal: 0,
      featuresGroups: [
        {
          label: '',
          features: [],
        },
      ],
    };
  }

  setChart(): void {
    this._features = [];
    this.datasets = [];
    this.xLabels = [];

    this.resetChartDescription();

    const startDate = moment.utc(
      this.dateRangeForm.get('startDate').value,
      'MM/YYYY'
    );
    const endDate = moment.utc(
      this.dateRangeForm.get('endDate').value,
      'MM/YYYY'
    );

    let params = {
      page: 1,
      startDate: startDate.toISOString(),
      endDate: endDate.endOf('month').toISOString(),
      patientUuid: this.route.snapshot.params['uuid'],
      featureIds: this.evaluation.features.map((item) => item.featureId),
    };

    const source$ = this.featureService.getFeatures(params).pipe(
      expand((res) => {
        params.page++;
        this._features = this._features.concat(res);
        return res.length < 50
          ? EMPTY
          : this.featureService.getFeatures(params);
      })
    );

    forkJoin([source$])
      .pipe(
        finalize(() => {
          this.loadingChart = false;
        })
      )
      .subscribe((res) => {
        let chartDataPoints = {};

        this.evaluation.features.forEach((feature) => {
          let groupedFeatures = this.featureService.groupFeatures(
            this._features,
            feature.featureId
          );
          chartDataPoints[feature.featureId] =
            this.chartService.setChartDataPoints(groupedFeatures);
        });

        if (Object.values(chartDataPoints).length > 0) {
          // Set labels
          const maxDataPoints = Object.values(chartDataPoints).reduce(
            (r: any, s: any) => (r.length > s.length ? r : s),
            0
          );
          this.xLabels = this.chartService.setXLabels(
            maxDataPoints as ChartDataPoint[]
          );

          // Set datasets
          this.evaluation.features.forEach((feature) => {
            const dataset = this.chartService.setDataset(
              feature,
              chartDataPoints[feature.featureId],
              feature.chartDatasetOption,
              [],
              this.xLabels,
              feature.min ??
                (chartDataPoints[feature.featureId].length
                  ? chartDataPoints[feature.featureId][0].min
                  : null),
              feature.max ??
                (chartDataPoints[feature.featureId].length
                  ? chartDataPoints[feature.featureId][0].max
                  : null),
              feature.isMaxLimited,
              feature.isMinLimited
            );
            this.datasets.push(dataset);
            this.chartDescription.featuresGroups[0].features.push({
              featureId: feature.featureId,
              hidden: feature.chartDatasetOption.hidden,
              data: dataset.data,
              unit: feature.chartDatasetOption.tooltip
                ? feature.chartDatasetOption.tooltip.unit
                : '',
              isPercentage:
                feature.featureId == FeatureService.FEATURE_CPS_STD_ACC,
              label: Array.isArray(feature.label)
                ? feature.label[0]
                : feature.label,
            });
          });
          this.chartDescription.featuresGroups[0].features.forEach(
            (dataset, i) => {
              if (
                this.chartDescription.featuresGroups[0].features[i].data.filter(
                  (item) => item.value !== null
                ).length > this.chartDescription.measuringPointsTotal
              ) {
                this.chartDescription.measuringPointsTotal =
                  this.chartDescription.featuresGroups[0].features[
                    i
                  ].data.filter((item) => item.value !== null).length;
              }
            }
          );
        }
      });
  }

  updateChartDescriptionFeature(chartDataset: ChartDataset): void {
    let feature = this.chartDescription.featuresGroups[0].features.find(
      (item) => item.featureId == chartDataset.id
    );
    feature.hidden = chartDataset.hidden;
  }
}
